import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Plus, Sparkles } from "lucide-react";
import { CompanyLogo } from "@/components/business/CompanyLogo";
import { ProviderDetail } from "@/components/business/ProviderDetail";
import { ProviderForm } from "@/components/business/ProviderForm";
import { ProviderLogo } from "@/components/business/ProviderLogo";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { useFormat } from "@/lib/format";

const Star = ({ center, items, onPick }) => {
  const { money } = useFormat();
  const n = items.length;
  const R = n > 8 ? 40 : 36;
  const max = Math.max(...items.map((i) => i.monthly_cost || 0), 1);
  const pos = (i) => { const a = (2 * Math.PI * i) / n - Math.PI / 2; return { x: 50 + R * Math.cos(a), y: 50 + R * Math.sin(a) }; };
  return (
    <div className="relative mx-auto aspect-square w-full max-w-[620px]" data-testid="provider-star">
      <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
        {items.map((p, i) => { const { x, y } = pos(i); return (
          <motion.line key={p.id} x1="50" y1="50" x2={x} y2={y} stroke="hsl(var(--primary))" strokeOpacity={p.deadline?.days_left >= 0 && p.deadline.days_left <= 30 ? 0.9 : 0.3}
            strokeDasharray={p.deadline?.days_left >= 0 && p.deadline.days_left <= 30 ? "1.2 1" : undefined}
            strokeWidth={0.25 + 0.9 * ((p.monthly_cost || 0) / max)} initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.1 + i * 0.06, duration: 0.6 }} />); })}
      </svg>
      <div className="absolute left-1/2 top-1/2 w-[24%] -translate-x-1/2 -translate-y-1/2">
      <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-full bg-[#142F39] p-3 text-center text-white shadow-xl" data-testid="provider-star-center">
        {center.logo}
        <span className="line-clamp-2 text-[9px] font-bold leading-tight sm:text-xs">{center.name}</span>
      </motion.div>
      </div>
      {items.map((p, i) => { const { x, y } = pos(i); const soon = p.deadline?.days_left >= 0 && p.deadline.days_left <= 30; return (
        <div key={p.id} className="absolute z-10 w-[23%] -translate-x-1/2 -translate-y-1/2 sm:w-[19%]" style={{ left: `${x}%`, top: `${y}%` }}>
        <motion.button type="button" onClick={() => onPick(p)} initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 + i * 0.06 }}
          whileHover={{ scale: 1.08 }} data-testid={`provider-node-${p.id}`}
          className="flex w-full flex-col items-center gap-0.5 text-center">
          <span className={`relative rounded-2xl bg-card p-1 shadow-md ring-2 ${soon ? "ring-destructive" : "ring-border"}`}><ProviderLogo p={p} className="h-8 w-8 sm:h-12 sm:w-12" />
            {soon && <span className="absolute -right-1 -top-1 h-3 w-3 animate-ping rounded-full bg-destructive" />}</span>
          <span className="flex flex-col items-center rounded-lg bg-card/95 px-1 py-0.5 shadow-sm">
          <span className="text-[8px] font-semibold uppercase tracking-wide text-muted-foreground sm:text-[10px]">{p.service_label}</span>
          <span className="line-clamp-2 text-[10px] font-bold leading-tight sm:line-clamp-1 sm:text-xs">{p.provider}</span>
          {p.monthly_cost != null && <span className="num text-[9px] text-muted-foreground sm:text-[11px]">{money(p.monthly_cost)}/mois</span>}</span>
        </motion.button></div>); })}
    </div>
  );
};

const Suggestions = ({ list, onAdd }) => {
  const { money, date } = useFormat();
  if (!list.length) return null;
  return (
    <section className="card-surface space-y-3 p-5" data-testid="provider-suggestions">
      <h2 className="flex items-center gap-2 font-bold"><Sparkles className="h-4 w-4 text-primary" /> Détectés dans vos dépenses</h2>
      <ul className="divide-y divide-border">{list.map((s) => (
        <li key={`${s.provider}-${s.service}`} className="flex items-center gap-3 py-2 text-sm" data-testid={`provider-suggestion-${s.provider}`}>
          <ProviderLogo p={s} className="h-9 w-9" />
          <div className="min-w-0 flex-1"><div className="truncate font-bold">{s.provider}</div><div className="text-xs text-muted-foreground">{s.service_label} · ~{money(s.monthly_cost)}/mois · vu depuis le {date(s.seen_since)}</div></div>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => onAdd(s)} data-testid={`provider-suggestion-add-${s.provider}`}><Plus className="mr-1 h-3.5 w-3.5" />Ajouter</Button></li>))}</ul>
    </section>
  );
};

export const ProviderMap = ({ base, canEdit, center, emptyText }) => {
  const qc = useQueryClient();
  const { money } = useFormat();
  const [open, setOpen] = useState(null);
  const key = ["providers", base];
  const { data } = useQuery({ queryKey: key, queryFn: () => api.get(`${base}/providers`).then((r) => r.data) });
  if (!data) return <div className="h-96 animate-pulse rounded-3xl bg-muted" />;
  const current = open?.mode === "view" ? data.items.find((p) => p.id === open.p.id) : null;
  const refresh = (close) => { qc.invalidateQueries({ queryKey: key }); if (close === true) setOpen(null); };
  return (
    <div className="space-y-5" data-testid="biz-map">
      <section className="card-surface space-y-4 p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[14rem] flex-1"><p className="eyebrow">Cartographie</p><p className="text-sm"><b className="num" data-testid="provider-total">{money(data.total_monthly)}</b> / mois chez {data.items.length} fournisseur(s)
            {data.saving_year > 0 && <> · <b className="text-success" data-testid="provider-saving-total">jusqu'à {money(data.saving_year)}/an d'économies</b></>}</p></div>
          {canEdit && <Button size="sm" className="rounded-full" onClick={() => setOpen({ mode: "form", p: null })} data-testid="provider-add"><Plus className="mr-1 h-4 w-4" />Ajouter un fournisseur</Button>}
        </div>
        {data.items.length ? <Star center={center} items={data.items} onPick={(p) => setOpen({ mode: "view", p })} />
          : <p className="rounded-xl bg-muted p-6 text-center text-sm text-muted-foreground" data-testid="provider-empty">{canEdit ? emptyText : "Aucun fournisseur enregistré pour l'instant."}</p>}
      </section>
      {canEdit && <Suggestions list={data.suggestions} onAdd={(s) => setOpen({ mode: "form", p: s })} />}
      <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg" data-testid="provider-sheet">
          <SheetHeader><SheetTitle>{open?.mode === "form" ? (open.p?.id ? "Modifier le fournisseur" : "Nouveau fournisseur") : "Fiche fournisseur"}</SheetTitle></SheetHeader>
          <div className="mt-4">{open?.mode === "form" ? <ProviderForm base={base} services={data.services} initial={open.p} onDone={() => { refresh(); setOpen(null); }} />
            : current && <ProviderDetail p={current} base={base} canEdit={canEdit} onEdit={() => setOpen({ mode: "form", p: current })} onChange={refresh} />}</div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export const BizMap = ({ company }) => (
  <ProviderMap base={`/business/companies/${company.id}`} canEdit={company.my_role !== "viewer"}
    center={{ name: company.name, logo: <CompanyLogo company={company} className="h-7 w-7 sm:h-10 sm:w-10" /> }}
    emptyText="Ajoutez vos fournisseurs principaux (électricité, internet, assurance…) ou reprenez ceux détectés ci-dessous." />
);
